import castArray from "lodash-es/castArray.js";
import compact from "lodash-es/compact.js";
import findLastIndex from "lodash-es/findLastIndex.js";
import isEqual from "lodash-es/isEqual.js";
import isNull from "lodash-es/isNull.js";
import isUndefined from "lodash-es/isUndefined.js";
import last from "lodash-es/last.js";
import uniqWith from "lodash-es/uniqWith.js";
import config from "./default-config.js";
export default class Serializer {
    filterConfig;
    helpers = {
        rightTrimArray(items) {
            const lastPresentIndex = findLastIndex(items, (v) => !isUndefined(v));
            if (lastPresentIndex < 0)
                return [];
            return items.slice(0, lastPresentIndex + 1);
        },
        wrapArray(value) {
            if (isUndefined(value) || isNull(value))
                return [];
            return castArray(value);
        },
    };
    constructor(filterConfig = config) {
        this.filterConfig = filterConfig;
    }
    serialize(variables) {
        const path = this.getPath(variables);
        const query = this.getQueryParams(variables);
        const result = {};
        if (path)
            result.path = path;
        if (query)
            result.query = query;
        return result;
    }
    // Path Params
    getPath(variables) {
        const pathSegments = `/${this.getPathSegments(variables)}`;
        if (this.filterConfig.pathPrefix) {
            return `${this.filterConfig.pathPrefix}${pathSegments}`;
        }
        return pathSegments;
    }
    getPathSegments(variables) {
        const segments = [
            ...this.getCategoryPathSegments(compact(variables.filter?.productCategoryPaths || [])),
            this.getFilterPathSegment(variables.filter),
            this.getPagePathSegment(variables.pagination),
        ];
        return compact(segments).join(this.filterConfig.separators.path);
    }
    getCategoryPathSegments(paths) {
        if (!paths.length)
            return [];
        const pathSegments = paths.map((path) => path.split(this.filterConfig.separators.path));
        const segLength = pathSegments[0].length;
        if (!pathSegments.every((s) => s.length === segLength)) {
            throw new Error("Multiple categories must have a common parent in the product " +
                `category filter (got: ${paths})`);
        }
        const parentCategories = uniqWith(pathSegments.map((s) => s.slice(0, segLength - 1)), isEqual);
        if (parentCategories.length > 1) {
            throw new Error("Multiple categories must have a common parent in the product " +
                `category filter (got: ${paths})`);
        }
        const childCategorySlugs = pathSegments.map((s) => last(s)).sort();
        return [
            ...parentCategories[0],
            childCategorySlugs.join(this.filterConfig.separators.item),
        ];
    }
    getFilterPathSegment(filterValues) {
        if (!filterValues)
            return undefined;
        const mappedValues = this.filterConfig.filters.map((filter) => this.getFilterValue(filterValues, filter));
        let segments = this.helpers.rightTrimArray(mappedValues);
        if (!segments.length)
            return undefined;
        segments = segments.map((value, index) => {
            const filter = Object.values(this.filterConfig.filters)[index] || {};
            return value || filter?.placeholder || "";
        });
        const str = segments.join(this.filterConfig.separators.filter);
        return `${this.filterConfig.prefixes.filter}${str}`;
    }
    getFilterValue(filterValues, filter) {
        const values = this.helpers.wrapArray(filterValues[filter.name]);
        if (!values.length)
            return undefined;
        const prefix = filter.prefix;
        const valueStr = values.sort().join(this.filterConfig.separators.item);
        if (prefix)
            return `${prefix}${valueStr}`;
        return valueStr;
    }
    getPagePathSegment(pagination) {
        if (!pagination)
            return undefined;
        const { page } = pagination;
        if (!page || page === 1)
            return undefined;
        return `${this.filterConfig.prefixes.page}${page}`;
    }
    // Query Params
    parseQueryParams(variables) {
        const parameters = this.filterConfig.queryParameters || [];
        const parsedFilters = {};
        parameters.forEach((parameter) => {
            if (!variables.filter)
                return;
            const value = variables.filter[parameter.name];
            if (value === undefined ||
                value === "" ||
                (Array.isArray(value) && value.length === 0))
                return;
            if (parameter.serializer) {
                const result = parameter.serializer(value);
                if (result === undefined)
                    return;
                parsedFilters[parameter.name] = result;
                return;
            }
            parsedFilters[parameter.name] = value;
        });
        return parsedFilters;
    }
    getQueryParams(variables) {
        return {
            ...this.parseQueryParams(variables),
            minPrice: this.getMinPrice(variables.filter?.onetimePrice),
            maxPrice: this.getMaxPrice(variables.filter?.onetimePrice),
            pageSize: this.getPageSize(variables.pagination),
            search: variables?.search,
            sort: this.getSort(variables.sort),
        };
    }
    getMinPrice(onetimePrice) {
        if (!onetimePrice || !onetimePrice.min)
            return undefined;
        return onetimePrice.min.toString();
    }
    getMaxPrice(onetimePrice) {
        if (!onetimePrice || !onetimePrice.max)
            return undefined;
        return onetimePrice.max.toString();
    }
    getPageSize(pagination) {
        const pageSize = pagination?.size || undefined;
        if (!pageSize || pageSize === this.filterConfig.defaultPageSize)
            return undefined;
        return pageSize.toString();
    }
    getSort(sort) {
        const defaultSort = this.filterConfig.sortMap[this.filterConfig.defaultSort];
        if (isEqual(sort, defaultSort))
            return undefined;
        return Object.keys(this.filterConfig.sortMap).find((name) => isEqual(sort, this.filterConfig.sortMap[name]));
    }
}
